import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Upload,
    Loader2,
    Trash2,
    ArrowUp,
    ArrowDown,
    ImageIcon,
    GripVertical,
} from 'lucide-react';
import { adminApi } from '@/lib/api';
import { uploadImage, deleteRemoteFile } from '@/lib/uploader';
import { cn } from '@/lib/utils';

/**
 * AdminPartnersPage — logo-first bulk manager.
 *
 * Workflow:
 * 1. Admin drops (or selects) multiple images.
 * 2. Each file is uploaded to Bunny CDN, then a new Partner record
 *    is created via POST /api/admin/partners with only { logo_url, order }.
 * 3. Existing partners are shown as a responsive grid of logo tiles with
 *    drag-and-drop reorder (native HTML5 DnD) plus up/down/delete controls.
 */
export default function AdminPartnersPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    const [dropIndex, setDropIndex] = useState(null);
    const [reordering, setReordering] = useState(false);
    const inputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const data = await adminApi.list('partners');
            setItems(Array.isArray(data) ? data : []);
        } catch {
            toast.error('Failed to load partners. Are you signed in as admin?');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const sorted = useMemo(
        () =>
            [...items].sort(
                (a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.id > b.id ? 1 : -1),
            ),
        [items],
    );

    const nextOrder = useMemo(() => {
        if (!items.length) return 1;
        return Math.max(...items.map((i) => Number(i.order || 0))) + 1;
    }, [items]);

    // ---------------------------------------------------------------
    // Bulk upload flow
    // ---------------------------------------------------------------
    const handleFiles = async (files) => {
        if (!files || !files.length) return;
        const imageFiles = Array.from(files).filter((f) =>
            f.type?.startsWith('image/'),
        );
        if (!imageFiles.length) {
            toast.error('Please choose image files only');
            return;
        }
        setBusy(true);
        setProgress({ done: 0, total: imageFiles.length });
        let created = 0;
        let baseOrder = nextOrder;
        for (const file of imageFiles) {
            try {
                const res = await uploadImage(file, 'partners');
                await adminApi.create('partners', {
                    logo_url: res.url,
                    order: baseOrder,
                });
                baseOrder += 1;
                created += 1;
            } catch (e) {
                const msg =
                    e?.response?.data?.detail || e?.message || 'Upload failed';
                toast.error(`Failed: ${file.name} — ${msg}`);
            } finally {
                setProgress((p) => ({ ...p, done: p.done + 1 }));
            }
        }
        setBusy(false);
        setProgress({ done: 0, total: 0 });
        if (created) {
            toast.success(
                `Uploaded ${created} logo${created === 1 ? '' : 's'}`,
            );
        }
        await load();
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (busy) return;
        handleFiles(e.dataTransfer?.files);
    };

    const onPickClick = () => {
        if (busy) return;
        inputRef.current?.click();
    };

    // ---------------------------------------------------------------
    // Reorder / delete
    // ---------------------------------------------------------------
    const move = async (idx, direction) => {
        const target = idx + direction;
        if (target < 0 || target >= sorted.length) return;
        const a = sorted[idx];
        const b = sorted[target];
        const aOrder = Number(a.order ?? idx);
        const bOrder = Number(b.order ?? target);
        // Optimistic UI
        setItems((prev) =>
            prev.map((it) => {
                if (it.id === a.id) return { ...it, order: bOrder };
                if (it.id === b.id) return { ...it, order: aOrder };
                return it;
            }),
        );
        try {
            await Promise.all([
                adminApi.update('partners', a.id, { order: bOrder }),
                adminApi.update('partners', b.id, { order: aOrder }),
            ]);
        } catch {
            toast.error('Reorder failed');
            load();
        }
    };

    /**
     * Move item at `from` index in the currently-sorted array to `to`.
     * After the move, rebase `order` values as 1..N and persist any changes.
     */
    const dropReorder = async (from, to) => {
        if (from == null || to == null || from === to) return;
        if (from < 0 || from >= sorted.length) return;
        if (to < 0 || to >= sorted.length) return;

        const reordered = [...sorted];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);

        // Rebase every item's `order` field to its new 1-based index.
        const nextItems = reordered.map((it, i) => ({ ...it, order: i + 1 }));
        // Detect which items actually changed order so we only PUT what's needed.
        const changed = nextItems.filter((it) => {
            const original = items.find((x) => x.id === it.id);
            return !original || Number(original.order ?? 0) !== it.order;
        });

        // Optimistic UI update.
        setItems(nextItems);
        if (!changed.length) return;
        setReordering(true);
        try {
            await Promise.all(
                changed.map((it) =>
                    adminApi.update('partners', it.id, { order: it.order }),
                ),
            );
            toast.success('Order saved');
        } catch {
            toast.error('Reorder failed');
            load();
        } finally {
            setReordering(false);
        }
    };

    // Native HTML5 drag handlers (per-tile)
    const onTileDragStart = (idx) => (e) => {
        setDragIndex(idx);
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            // Firefox needs data set to fire dragover on siblings.
            try {
                e.dataTransfer.setData('text/plain', String(idx));
            } catch (_) {
                // ignore
            }
        }
    };
    const onTileDragOver = (idx) => (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        if (dropIndex !== idx) setDropIndex(idx);
    };
    const onTileDrop = (idx) => (e) => {
        e.preventDefault();
        const from = dragIndex;
        setDragIndex(null);
        setDropIndex(null);
        if (from == null || from === idx) return;
        dropReorder(from, idx);
    };
    const onTileDragEnd = () => {
        setDragIndex(null);
        setDropIndex(null);
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        const target = confirmDelete;
        setConfirmDelete(null);
        try {
            // best-effort CDN delete
            if (target.logo_url) {
                try {
                    await deleteRemoteFile(target.logo_url);
                } catch (_) {
                    // ignore — record may reference an external URL
                }
            }
            await adminApi.remove('partners', target.id);
            toast.success('Deleted');
            setItems((prev) => prev.filter((it) => it.id !== target.id));
        } catch {
            toast.error('Delete failed');
            load();
        }
    };

    // ---------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------
    return (
        <div data-testid="admin-partners-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="font-display text-2xl font-semibold">
                        Partners
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Upload multiple partner logos at once — no other details
                        required. Drag tiles to reorder.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {reordering && (
                        <span
                            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
                            data-testid="admin-partners-saving"
                        >
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Saving order…
                        </span>
                    )}
                    <Badge variant="outline" data-testid="admin-partners-count">
                        {items.length} logo{items.length === 1 ? '' : 's'}
                    </Badge>
                </div>
            </div>

            {/* Bulk upload zone */}
            <Card className="bg-card border-border mb-6">
                <CardContent className="p-4">
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={onPickClick}
                        onKeyDown={(e) =>
                            (e.key === 'Enter' || e.key === ' ') && onPickClick()
                        }
                        onDragOver={(e) => {
                            e.preventDefault();
                            if (!busy) setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        className={cn(
                            'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 cursor-pointer text-center transition-colors',
                            dragOver
                                ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5'
                                : 'border-border bg-[hsl(var(--secondary))]/30 hover:bg-[hsl(var(--secondary))]/60',
                            busy && 'pointer-events-none opacity-70',
                        )}
                        data-testid="admin-partners-dropzone"
                    >
                        <div className="h-12 w-12 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center mb-3">
                            {busy ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                <Upload className="h-6 w-6" />
                            )}
                        </div>
                        <div className="text-base font-medium text-foreground">
                            {busy
                                ? `Uploading ${progress.done} / ${progress.total}…`
                                : 'Click or drop partner logos here'}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            Multi-select supported · JPG, PNG, WebP, SVG · up to
                            8 MB each
                        </div>
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                            className="hidden"
                            onChange={(e) => handleFiles(e.target.files)}
                            data-testid="admin-partners-file-input"
                        />
                        {busy && progress.total > 0 && (
                            <div className="w-full max-w-sm mt-4">
                                <Progress
                                    value={(progress.done / progress.total) * 100}
                                    className="h-1.5"
                                />
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Existing partners grid */}
            <Card className="bg-card border-border">
                <CardContent className="p-4">
                    {loading ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <Skeleton key={i} className="h-28 w-full" />
                            ))}
                        </div>
                    ) : sorted.length === 0 ? (
                        <div className="py-12 text-center text-sm text-muted-foreground">
                            <ImageIcon className="h-8 w-8 mx-auto mb-3 opacity-50" />
                            No partner logos yet. Drop a few images above to get
                            started.
                        </div>
                    ) : (
                        <div
                            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
                            data-testid="admin-partners-grid"
                        >
                            {sorted.map((p, idx) => {
                                const isDragging = dragIndex === idx;
                                const isDropTarget =
                                    dropIndex === idx && dragIndex !== idx;
                                return (
                                    <Card
                                        key={p.id}
                                        draggable={!busy && !reordering}
                                        onDragStart={onTileDragStart(idx)}
                                        onDragOver={onTileDragOver(idx)}
                                        onDrop={onTileDrop(idx)}
                                        onDragEnd={onTileDragEnd}
                                        onDragLeave={() => {
                                            if (dropIndex === idx) setDropIndex(null);
                                        }}
                                        className={cn(
                                            'overflow-hidden bg-card border-border transition-all select-none',
                                            !busy && !reordering && 'cursor-grab active:cursor-grabbing',
                                            isDragging && 'opacity-40 scale-[0.98]',
                                            isDropTarget &&
                                                'ring-2 ring-[hsl(var(--primary))] ring-offset-2 ring-offset-background',
                                        )}
                                        data-testid={`admin-partners-tile-${p.id}`}
                                        data-drag-index={idx}
                                    >
                                        <CardContent className="p-0">
                                            <div className="relative aspect-[4/3] bg-white flex items-center justify-center p-3 border-b border-border">
                                                {/* Drag handle affordance */}
                                                <span
                                                    className="absolute top-1.5 left-1.5 inline-flex items-center justify-center h-6 w-6 rounded-md bg-black/5 text-muted-foreground pointer-events-none"
                                                    aria-hidden="true"
                                                >
                                                    <GripVertical className="h-3.5 w-3.5" />
                                                </span>
                                                {p.logo_url ? (
                                                    <img
                                                        src={p.logo_url}
                                                        alt=""
                                                        draggable={false}
                                                        className="max-h-full max-w-full object-contain pointer-events-none"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between px-2 py-1.5">
                                                <div className="flex items-center gap-0.5">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => move(idx, -1)}
                                                        disabled={idx === 0 || reordering}
                                                        title="Move up"
                                                        data-testid={`admin-partners-up-${p.id}`}
                                                    >
                                                        <ArrowUp className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => move(idx, 1)}
                                                        disabled={
                                                            idx === sorted.length - 1 || reordering
                                                        }
                                                        title="Move down"
                                                        data-testid={`admin-partners-down-${p.id}`}
                                                    >
                                                        <ArrowDown className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <span className="text-[10px] text-muted-foreground ml-1">
                                                        #{idx + 1}
                                                    </span>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 text-destructive hover:text-destructive"
                                                    onClick={() =>
                                                        setConfirmDelete(p)
                                                    }
                                                    title="Delete"
                                                    data-testid={`admin-partners-delete-${p.id}`}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Delete confirm */}
            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this logo?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove the logo from the CMS
                            and, where possible, from the CDN.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-partners-delete-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="admin-partners-delete-confirm"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
